import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { normalizeColorHexForCompare } from '../../utils/annualTimetable';
import {
  checklistCheckedFromApplyResults,
  type HoursAdjustChecklistApplyResult,
} from '../../utils/annualTimetableHoursAdjustApply';
import type {
  HoursAdjustChecklist,
  HoursAdjustPhase2Result,
} from '../../utils/annualTimetableHoursAdjustPlan';
import { patchHoursAdjustModalSessionChecklist } from '../../utils/hoursAdjustModalSessionCache';
import {
  PAGE_ZOOM_CHANGED_EVENT,
  clientPointToLayoutPoint,
  clientRectToLayoutRect,
  getLayoutViewportSize,
} from '../../utils/pageZoom';

export type HoursAdjustChecklistFloatingModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  gradeId: string;
  classTitle: string;
  checklist: HoursAdjustChecklist;
  phase2: HoursAdjustPhase2Result;
  dedicatedColors?: string[];
  baseExcludeColors?: string[];
  programColorLabels?: {
    dedicated?: Record<string, string>;
    baseExclude?: Record<string, string>;
  };
  initialSkipColorHexes?: string[];
  initialChecklistChecked?: Record<string, boolean>;
  onApplyChecklist: (
    phase2: HoursAdjustPhase2Result,
    options?: {
      skipColorHexes?: string[];
      checklistTransfers?: HoursAdjustChecklist['transfers'];
    }
  ) => HoursAdjustChecklistApplyResult | null;
};

function uniqueColorHexes(colors: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  colors.forEach((hex) => {
    const n = normalizeColorHexForCompare(hex);
    if (!n || seen.has(n)) return;
    seen.add(n);
    out.push(hex.trim());
  });
  return out;
}

function isSkipColorSelected(selected: string[], hex: string): boolean {
  const n = normalizeColorHexForCompare(hex);
  if (!n) return false;
  return selected.some((s) => normalizeColorHexForCompare(s) === n);
}

function lookupProgramColorLabel(
  map: Record<string, string> | undefined,
  hex: string
): string {
  if (!map) return '';
  const n = normalizeColorHexForCompare(hex);
  if (!n) return '';
  const direct = map[n] ?? map[hex.trim()];
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  for (const [key, value] of Object.entries(map)) {
    if (normalizeColorHexForCompare(key) === n && value.trim()) return value.trim();
  }
  return '';
}

function clampPosition(x: number, y: number, width: number, height: number) {
  const pad = 8;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const maxX = Math.max(pad, vw - width - pad);
  const maxY = Math.max(pad, vh - height - pad);
  return {
    x: Math.min(maxX, Math.max(pad, x)),
    y: Math.min(maxY, Math.max(pad, y)),
  };
}

function defaultChecklistPosition(width: number, height: number) {
  const { width: vw } = getLayoutViewportSize();
  return clampPosition(vw - width - 24, 96, width, height);
}

const PANEL_W = 280;

const HoursAdjustChecklistFloatingModal: React.FC<HoursAdjustChecklistFloatingModalProps> = ({
  open,
  onClose,
  workspaceId,
  gradeId,
  classTitle,
  checklist,
  phase2,
  dedicatedColors = [],
  baseExcludeColors = [],
  programColorLabels,
  initialSkipColorHexes = [],
  initialChecklistChecked = {},
  onApplyChecklist,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => defaultChecklistPosition(PANEL_W, 320));
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const [checklistChecked, setChecklistChecked] = useState<Record<string, boolean>>(
    initialChecklistChecked
  );
  const [applySkipColorHexes, setApplySkipColorHexes] = useState<string[]>(() =>
    uniqueColorHexes(initialSkipColorHexes)
  );
  const [checklistApplyMessage, setChecklistApplyMessage] = useState<string | null>(null);
  const [curriculumFillNotice, setCurriculumFillNotice] = useState<string | null>(null);
  const [checklistApplying, setChecklistApplying] = useState(false);
  /** 자동 반영 1회 실행 후 재실행 방지(재실행 시 완료 체크가 풀림) */
  const [applyCompleted, setApplyCompleted] = useState(false);

  const dedicatedColorOptions = useMemo(
    () => uniqueColorHexes(dedicatedColors),
    [dedicatedColors]
  );
  const baseExcludeColorOptions = useMemo(
    () => uniqueColorHexes(baseExcludeColors),
    [baseExcludeColors]
  );

  useEffect(() => {
    if (!open) return;
    setChecklistChecked(initialChecklistChecked);
    setApplySkipColorHexes(uniqueColorHexes(initialSkipColorHexes));
    setChecklistApplyMessage(null);
    setCurriculumFillNotice(null);
    setApplyCompleted(false);
    setPosition(defaultChecklistPosition(PANEL_W, 320));
  }, [open, checklist, initialChecklistChecked, initialSkipColorHexes]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const reclamp = () => {
      const el = panelRef.current;
      const w = el?.offsetWidth ?? PANEL_W;
      const h = el?.offsetHeight ?? 320;
      setPosition((prev) => clampPosition(prev.x, prev.y, w, h));
    };
    reclamp();
    window.addEventListener('resize', reclamp);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, reclamp);
    return () => {
      window.removeEventListener('resize', reclamp);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, reclamp);
    };
  }, [open, checklist.transfers.length]);

  useEffect(() => {
    if (!open || !dragging) return undefined;
    const onMove = (e: PointerEvent) => {
      const el = panelRef.current;
      const w = el?.offsetWidth ?? PANEL_W;
      const h = el?.offsetHeight ?? 320;
      const pt = clientPointToLayoutPoint(e.clientX, e.clientY);
      setPosition(
        clampPosition(pt.x - dragOffsetRef.current.x, pt.y - dragOffsetRef.current.y, w, h)
      );
    };
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [open, dragging]);

  const persistChecklistState = (
    nextChecked: Record<string, boolean>,
    nextSkip: string[]
  ) => {
    patchHoursAdjustModalSessionChecklist(workspaceId, gradeId, {
      checklist,
      checklistChecked: nextChecked,
      applySkipColorHexes: nextSkip,
    });
  };

  const toggleChecklistItem = (id: string) => {
    setChecklistChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistChecklistState(next, applySkipColorHexes);
      return next;
    });
  };

  const toggleApplySkipColor = (hex: string) => {
    const n = normalizeColorHexForCompare(hex);
    if (!n) return;
    setApplySkipColorHexes((prev) => {
      const selected = isSkipColorSelected(prev, hex);
      const next = selected
        ? prev.filter((s) => normalizeColorHexForCompare(s) !== n)
        : uniqueColorHexes([...prev, hex]);
      persistChecklistState(checklistChecked, next);
      return next;
    });
  };

  const applyChecklistToTimetable = () => {
    if (checklist.transfers.length === 0 || checklistApplying || applyCompleted) return;
    setChecklistApplying(true);
    try {
      const result = onApplyChecklist(phase2, {
        skipColorHexes: applySkipColorHexes,
        checklistTransfers: checklist.transfers,
      });
      if (!result) {
        setChecklistApplyMessage('반영에 실패했습니다.');
        return;
      }
      const checkedNext = checklistCheckedFromApplyResults(checklist.transfers, result.results);
      setChecklistChecked((prev) => {
        const merged = { ...prev, ...checkedNext };
        persistChecklistState(merged, applySkipColorHexes);
        return merged;
      });
      const failedCount = checklist.transfers.filter((t) => !checkedNext[t.id]).length;
      if (result.appliedCount === 0) {
        setChecklistApplyMessage(
          result.shortfallCount > 0
            ? `반영할 셀을 찾지 못했습니다. (부족 ${result.shortfallCount}시간 · 제외 색·고정P·편집불가 등). 미완료 ${failedCount}건`
            : '이미 목표와 같거나, 옮길 실수업 셀이 없습니다.'
        );
      } else {
        setChecklistApplyMessage(
          result.shortfallCount > 0 || failedCount > 0
            ? `${result.appliedCount}칸 반영 · 부족 ${result.shortfallCount}시간 · 미완료 ${failedCount}건. 저장하기로 DB에 반영하세요.`
            : `${result.appliedCount}칸을 반영했습니다. 저장하기로 DB에 반영하세요.`
        );
      }
      setCurriculumFillNotice(
        result.curriculumFillAppliedCount > 0
          ? "고정(행사) 프로그램에 '교과'로 표시된 시간표를 시수 부족분에 따라 임의 조정하였습니다.\n고정(행사) 프로그램의 성격에 맞는지 확인 후 조정이 필요한 경우 수동으로 시간표를 조정해주세요"
          : null
      );
      // 1회 실행 후 잠금 — 재실행하면 이미 반영된 상태에서 항목이 전부 실패로 표시됨
      setApplyCompleted(true);
    } finally {
      setChecklistApplying(false);
    }
  };

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = clientRectToLayoutRect(el.getBoundingClientRect());
    const pt = clientPointToLayoutPoint(e.clientX, e.clientY);
    dragOffsetRef.current = { x: pt.x - rect.left, y: pt.y - rect.top };
    setDragging(true);
    e.preventDefault();
  };

  const renderColorGroup = (
    label: string,
    colors: string[],
    labelMap: Record<string, string> | undefined,
    keyPrefix: string
  ) => (
    <div
      className="cp-base-timetable-dedicated-palette cp-annual-hours-adjust-skip-palette"
      aria-label={label}
    >
      <span className="cp-base-timetable-dedicated-label">{label}</span>
      {colors.length === 0 ? (
        <span className="cp-annual-hours-adjust-skip-colors-empty">없음</span>
      ) : (
        colors.map((hex) => {
          const selected = isSkipColorSelected(applySkipColorHexes, hex);
          const colorLabel = lookupProgramColorLabel(labelMap, hex);
          return (
            <span key={`${keyPrefix}-${hex}`} className="cp-annual-project-toolbar-swatch-wrap">
              <button
                type="button"
                className={
                  selected
                    ? 'cp-dedicated-color-btn is-skip-selected'
                    : 'cp-dedicated-color-btn is-skip-unselected'
                }
                onClick={() => toggleApplySkipColor(hex)}
                title={colorLabel ? undefined : hex}
                aria-pressed={selected}
                aria-label={
                  colorLabel
                    ? `${label} ${colorLabel}${selected ? ' 제외 중' : ''}`
                    : `${label} ${hex}${selected ? ' 제외 중' : ''}`
                }
              >
                <span
                  className="cp-dedicated-color-btn-inner"
                  style={{ backgroundColor: hex }}
                  aria-hidden
                />
              </button>
              {colorLabel ? (
                <span className="cp-annual-project-toolbar-tooltip" role="tooltip">
                  {colorLabel}
                </span>
              ) : null}
            </span>
          );
        })
      )}
    </div>
  );

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={
        dragging
          ? 'cp-hours-adjust-checklist-float is-dragging'
          : 'cp-hours-adjust-checklist-float'
      }
      style={{ left: position.x, top: position.y, width: PANEL_W }}
      role="dialog"
      aria-label={`시수 조정 체크리스트 · ${classTitle}`}
    >
      <div
        className="cp-hours-adjust-checklist-float-header"
        onPointerDown={onHeaderPointerDown}
      >
        <h3 className="cp-hours-adjust-checklist-float-title">
          시수 조정 체크리스트 · {classTitle}
        </h3>
        <button
          type="button"
          className="cp-hours-adjust-checklist-float-close"
          onClick={onClose}
          aria-label="닫기"
        >
          ×
        </button>
      </div>
      <div className="cp-hours-adjust-checklist-float-body">
        <p className="cp-annual-hours-adjust-checklist-meta">
          시간표를 보면서 이전할 항목을 확인·반영하세요. 헤더를 드래그해 위치를 옮길 수
          있습니다.
        </p>
        <div className="cp-hours-adjust-checklist-float-list-wrap">
          {checklist.transfers.length === 0 ? (
            <p className="cp-annual-hours-adjust-checklist-empty">
              옮길 시수가 없습니다. 원본과 조정 결과가 동일합니다.
            </p>
          ) : (
            <ul className="cp-annual-hours-adjust-checklist-list">
              {checklist.transfers.map((item) => {
                const checked = checklistChecked[item.id] === true;
                return (
                  <li key={item.id}>
                    <label
                      className={
                        checked
                          ? 'cp-annual-hours-adjust-checklist-item is-checked'
                          : 'cp-annual-hours-adjust-checklist-item'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleChecklistItem(item.id)}
                      />
                      <span
                        className={
                          checked
                            ? 'cp-annual-hours-adjust-checklist-text is-checked'
                            : 'cp-annual-hours-adjust-checklist-text'
                        }
                      >
                        <strong>{item.term}학기</strong>
                        {' · '}
                        <strong>{item.fromTrack}</strong>
                        {` ${item.hours}시간 → `}
                        <strong>{item.toTrack}</strong>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="cp-annual-hours-adjust-skip-colors" aria-label="자동 조정 제외 색상">
          <div className="cp-annual-hours-adjust-skip-colors-title">자동 조정 제외 색상</div>
          <p className="cp-annual-hours-adjust-skip-colors-hint">
            선택한 색의 칸은 자동 반영 때 수정하지 않습니다. (복수 선택, 기본 미선택)
          </p>
          <div className="cp-annual-hours-adjust-skip-colors-palettes">
            {renderColorGroup(
              '전담',
              dedicatedColorOptions,
              programColorLabels?.dedicated,
              'ded'
            )}
            {renderColorGroup(
              '라벨링 색상',
              baseExcludeColorOptions,
              programColorLabels?.baseExclude,
              'base'
            )}
          </div>
        </div>

        {checklist.transfers.length > 0 ? (
          <div className="cp-annual-hours-adjust-checklist-apply">
            <button
              type="button"
              className="cp-btn-primary"
              onClick={applyChecklistToTimetable}
              disabled={checklistApplying || applyCompleted}
              title={applyCompleted ? '이미 자동 반영을 실행했습니다' : undefined}
            >
              {checklistApplying
                ? '반영 중…'
                : applyCompleted
                  ? '반영 완료'
                  : '체크리스트 자동 반영'}
            </button>
            <p className="cp-annual-hours-adjust-checklist-apply-hint">
              선택한 제외 색 칸은 건드리지 않으며, 완전히 반영되지 않은 항목은 미완료로
              남습니다.
            </p>
            {checklistApplyMessage ? (
              <p className="cp-annual-hours-adjust-checklist-apply-msg" role="status">
                {checklistApplyMessage}
              </p>
            ) : null}
            {curriculumFillNotice ? (
              <p
                className="cp-annual-hours-adjust-checklist-apply-msg cp-annual-hours-adjust-checklist-curriculum-notice"
                role="status"
              >
                {curriculumFillNotice}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default HoursAdjustChecklistFloatingModal;
